import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// Pattern Matching Game
function PatternGame({ onComplete }) {
  const COLORS = ['🔴', '🔵', '🟡', '🟢', '🟣'];
  const [pattern] = useState(() => {
    const len = 4;
    return Array.from({ length: len }, () => COLORS[Math.floor(Math.random() * COLORS.length)]);
  });
  const [userPattern, setUserPattern] = useState([]);
  const [result, setResult] = useState(null);

  const handleTap = useCallback((color) => {
    if (result) return;
    const newPattern = [...userPattern, color];
    setUserPattern(newPattern);

    if (newPattern.length === pattern.length) {
      const correct = newPattern.every((c, i) => c === pattern[i]);
      setResult(correct);
      setTimeout(() => onComplete(correct), 1500);
    }
  }, [userPattern, pattern, result, onComplete]);

  return (
    <div style={{ textAlign: 'center' }}>
      <h3 style={{ fontFamily: 'var(--font-title)', marginBottom: 16, fontSize: '1.1rem' }}>
        Remember this pattern!
      </h3>
      <div style={{ fontSize: '2.5rem', letterSpacing: 8, marginBottom: 20, background: 'var(--bg)', padding: 16, borderRadius: 14 }}>
        {pattern.join(' ')}
      </div>
      <p style={{ color: 'var(--gray)', marginBottom: 16, fontSize: '0.85rem' }}>Now tap the same pattern:</p>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {['🔴', '🔵', '🟡', '🟢', '🟣'].map(c => (
          <button key={c} onClick={() => handleTap(c)} style={{
            width: 54, height: 54, fontSize: '1.8rem', border: '3px solid var(--border)',
            borderRadius: 14, background: 'white', cursor: 'pointer'
          }}>{c}</button>
        ))}
      </div>
      <div style={{ fontSize: '2rem', letterSpacing: 8, minHeight: 48 }}>
        {userPattern.join(' ')}
      </div>
      {result !== null && (
        <div style={{ marginTop: 12, fontSize: '1.5rem' }}>
          {result ? '🎉 Correct!' : '😅 Try again!'}
        </div>
      )}
    </div>
  );
}

// Find the Difference Game
function FindDifferenceGame({ onComplete }) {
  const EMOJI_SETS = [
    { sets: ['🐶🐶🐶🐱🐶', '🐶🐶🐶🐶🐶'], answer: 4, label: 'cat' },
    { sets: ['⭐⭐🌙⭐⭐', '⭐⭐⭐⭐⭐'], answer: 3, label: 'moon' },
    { sets: ['🍎🍎🍊🍎🍎', '🍎🍎🍎🍎🍎'], answer: 3, label: 'orange' }
  ];
  const [game] = useState(() => EMOJI_SETS[Math.floor(Math.random() * EMOJI_SETS.length)]);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);

  const handleSelect = (index) => {
    if (result !== null) return;
    setSelected(index);
    const correct = index === game.answer - 1;
    setResult(correct);
    setTimeout(() => onComplete(correct), 1500);
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <h3 style={{ fontFamily: 'var(--font-title)', marginBottom: 8, fontSize: '1.1rem' }}>
        Find the different one!
      </h3>
      <p style={{ color: 'var(--gray)', fontSize: '0.85rem', marginBottom: 20 }}>
        Tap the emoji that doesn't belong
      </p>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {game.sets[0].split('').filter(c => c.trim()).map((emoji, i) => (
          <button key={i} onClick={() => handleSelect(i)} style={{
            width: 60, height: 60, fontSize: '2rem',
            border: `3px solid ${selected === i ? (result ? 'var(--green)' : 'var(--red)') : 'var(--border)'}`,
            borderRadius: 14, background: selected === i ? (result ? '#E8F5E9' : '#FFEBEE') : 'white',
            cursor: result !== null ? 'default' : 'pointer', transition: 'all 0.2s'
          }}>{emoji}</button>
        ))}
      </div>
      {result !== null && (
        <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>
          {result ? '🎉 Correct! The ' + game.label + ' is different!' : '😅 Not quite! Look for the ' + game.label}
        </div>
      )}
    </div>
  );
}

export default function FocusScreen() {
  const navigate = useNavigate();
  const [game, setGame] = useState(null); // 'pattern' | 'difference'
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const MAX_ROUNDS = 3;

  const handleComplete = useCallback((correct) => {
    if (correct) setScore(s => s + 1);
    const nextRound = round + 1;
    if (nextRound >= MAX_ROUNDS) {
      setGameOver(true);
    } else {
      setRound(nextRound);
      // Alternate between games
      setGame(g => g === 'pattern' ? 'difference' : 'pattern');
    }
  }, [round]);

  const startGame = (type) => {
    setGame(type);
    setRound(0);
    setScore(0);
    setGameOver(false);
  };

  if (!game) {
    return (
      <div className="screen">
        <div className="screen-header" style={{ background: 'linear-gradient(135deg, #FC5C65, #EB3B5A)', paddingBottom: 16 }}>
          <button className="btn-back" onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.3)', color: 'white' }}>←</button>
          <h1 style={{ fontFamily: 'var(--font-title)', color: 'white', fontSize: '1.5rem' }}>🧩 Focus Training</h1>
        </div>
        <div className="screen-body" style={{ paddingTop: 20 }}>
          <p style={{ color: 'var(--gray)', marginBottom: 20, textAlign: 'center' }}>Train your brain! 🧠</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { type: 'pattern', icon: '🎨', title: 'Pattern Memory', desc: 'Remember and repeat the pattern!', color: '#FF9F43' },
              { type: 'difference', icon: '🔍', title: 'Find the Difference', desc: 'Spot the emoji that is different!', color: '#45AAF2' }
            ].map(g => (
              <div key={g.type} className="card card-clickable" onClick={() => startGame(g.type)} style={{ display: 'flex', gap: 14 }}>
                <div style={{ fontSize: '3rem' }}>{g.icon}</div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4 }}>{g.title}</div>
                  <div style={{ color: 'var(--gray)', fontSize: '0.85rem' }}>{g.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="screen-header" style={{ background: 'linear-gradient(135deg, #FC5C65, #EB3B5A)', paddingBottom: 16 }}>
        <button className="btn-back" onClick={() => setGame(null)} style={{ background: 'rgba(255,255,255,0.3)', color: 'white' }}>←</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: 'var(--font-title)', color: 'white', fontSize: '1.4rem' }}>🧩 Focus Training</h1>
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>Round {round + 1}/{MAX_ROUNDS} · Score: {score}</div>
        </div>
      </div>
      <div className="screen-body" style={{ paddingTop: 30 }}>
        {gameOver ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '5rem', marginBottom: 12 }}>{score === MAX_ROUNDS ? '🏆' : score >= 2 ? '🌟' : '💪'}</div>
            <h2 style={{ fontFamily: 'var(--font-title)', color: 'var(--purple)', marginBottom: 8 }}>
              {score === MAX_ROUNDS ? 'Perfect!' : 'Well done!'}
            </h2>
            <p style={{ color: 'var(--gray)', marginBottom: 24 }}>You got {score} out of {MAX_ROUNDS} correct!</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => startGame(game)}>Play Again</button>
              <button className="btn" style={{ flex: 1, background: 'var(--bg)' }} onClick={() => setGame(null)}>Menu</button>
            </div>
          </div>
        ) : game === 'pattern' ? (
          <PatternGame key={round} onComplete={handleComplete} />
        ) : (
          <FindDifferenceGame key={round} onComplete={handleComplete} />
        )}
      </div>
    </div>
  );
}
